"use client";

import React from 'react';
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Briefcase, UserCheck, ArrowUp, ArrowDown, Minus } from "lucide-react";

interface StatsOverviewProps {
  dashboardData: {
    stats: {
      total_leads: number;
      total_projects: number;
      active_users: number;
      total_revenue: number;
      currency?: string;
    };
  } | null;
  loading: boolean;
}

type TrendType = 'up' | 'down' | 'neutral';

interface DashboardMetricCardProps {
  value: string;
  title: string;
  icon?: React.ElementType;
  trendChange?: string;
  trendType?: TrendType;
  className?: string;
}

const DashboardMetricCard: React.FC<DashboardMetricCardProps> = ({
  value,
  title,
  icon: IconComponent,
  trendChange,
  trendType = 'neutral',
  className,
}) => {
  const TrendIcon = trendType === 'up' ? ArrowUp : trendType === 'down' ? ArrowDown : Minus;
  const trendColorClass =
    trendType === 'up'
      ? "text-green-600 dark:text-green-400"
      : trendType === 'down'
      ? "text-red-600 dark:text-red-400"
      : "text-muted-foreground";

  return (
    <div className={cn("rounded-lg", className)}>
      <Card className="h-full transition-colors duration-200 bg-black/95">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-xs font-medium text-gray-400">
            {title}
          </CardTitle>
          {IconComponent && (
            <IconComponent className="h-4 w-4 text-gray-400" aria-hidden="true" />
          )}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-white mb-2">{value}</div>
          {trendChange && (
            <p className={cn("flex items-center text-xs font-medium", trendColorClass)}>
              <TrendIcon className="h-3 w-3 mr-1" aria-hidden="true" />
              {trendChange} {trendType === 'up' ? "increase" : trendType === 'down' ? "decrease" : "change"}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="rounded-lg">
    <Card className="h-full transition-colors duration-200 animate-pulse bg-gray-900/95">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="h-4 bg-gray-700 rounded w-20"></div>
        <div className="h-4 w-4 bg-gray-700 rounded"></div>
      </CardHeader>
      <CardContent>
        <div className="h-8 bg-gray-700 rounded w-16 mb-2"></div>
      </CardContent>
    </Card>
  </div>
);

export function StatsOverview({ dashboardData, loading }: StatsOverviewProps) {
  if (loading || !dashboardData) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-gray-500 font-medium mb-1">Overview</p>
            <h3 className="text-xs font-bold text-gray-900">Key Statistics</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  const stats = dashboardData.stats;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500 font-medium mb-1">Overview</p>
          <h3 className="text-xs font-bold text-gray-900">Key Statistics</h3>
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <DashboardMetricCard
          title="Total Leads"
          value={stats.total_leads.toLocaleString()}
          icon={Users}
        />
        <DashboardMetricCard
          title="Total Projects"
          value={stats.total_projects.toLocaleString()}
          icon={Briefcase}
        />
        <DashboardMetricCard
          title="Active Users"
          value={stats.active_users.toLocaleString()}
          icon={UserCheck}
        />
      </div>
    </div>
  );
}